import React from 'react';

import { LabelInput, Switch } from '@/components/ui/inputs';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { PermissionGuard, SidePanelFormLayout } from '@/components/layout';
import { useTranslation } from '@/i18n';
import { useEditSecurityGroupingForm } from '@/hooks/securityGrouping';
import type { SecurityGroupItem } from '@/api/domains/securityGrouping';


interface EditSecurityGroupingFormPanelProps {
  groupId: number;
  initialData: SecurityGroupItem;
  onSubmit?: () => void;
}

export const EditSecurityGroupingFormPanel: React.FC<EditSecurityGroupingFormPanelProps> = ({
  groupId,
  initialData,
  onSubmit,
}) => {
  const { t: translate } = useTranslation();

  const {
    codeValue,
    groupNameValue,
    sendsAttendanceStatusValue,
    handleCodeChange,
    handleGroupNameChange,
    handleSendsAttendanceStatusChange,
    handleSubmit,
    isSubmitting,
    isReady,
    errors,
    isSubmitted,
    metadata,
  } = useEditSecurityGroupingForm({ groupId, initialData, onSubmit });

  return (
    <SidePanelFormLayout onSubmit={handleSubmit} data-testid="edit-security-grouping-form">
      <SidePanelFormLayout.Fields>
        <LabelInput
          label={translate('securityGrouping:form.id')}
          value={String(metadata.id)}
          disabled
          data-testid="edit-security-grouping-id-input"
        />

        {metadata.isRexActive && (
          <LabelInput
            label={translate('securityGrouping:form.itemRex')}
            value={metadata.itemRex}
            disabled
            data-testid="edit-security-grouping-item-rex-input"
          />
        )}

        <LabelInput
          label={translate('securityGrouping:form.code')}
          value={codeValue}
          onChange={handleCodeChange}
          disabled={isSubmitting}
          color={errors.code && isSubmitted ? 'error' : 'default'}
          supportingText={
            errors.code?.message && isSubmitted
              ? translate(String(errors.code.message))
              : undefined
          }
          data-testid="edit-security-grouping-code-input"
        />

        <LabelInput
          label={translate('securityGrouping:form.groupName')}
          value={groupNameValue}
          onChange={handleGroupNameChange}
          required
          disabled={isSubmitting}
          color={errors.groupName && isSubmitted ? 'error' : 'default'}
          supportingText={
            errors.groupName?.message && isSubmitted
              ? translate(String(errors.groupName.message))
              : undefined
          }
          data-testid="edit-security-grouping-group-name-input"
        />

        <Switch
          label={translate('securityGrouping:form.sendsAttendanceStatus')}
          checked={sendsAttendanceStatusValue}
          onChange={handleSendsAttendanceStatusChange}
          disabled={isSubmitting}
          data-testid="edit-security-grouping-sends-attendance-status-switch"
        />

        <LabelInput
          label={translate('securityGrouping:form.createdDate')}
          value={metadata.createdDate}
          disabled
          data-testid="edit-security-grouping-created-date-input"
        />

        <LabelInput
          label={translate('securityGrouping:form.createdBy')}
          value={metadata.createdBy}
          disabled
          data-testid="edit-security-grouping-created-by-input"
        />

        <LabelInput
          label={translate('securityGrouping:form.modifiedDate')}
          value={metadata.modifiedDate}
          disabled
          data-testid="edit-security-grouping-modified-date-input"
        />

        <LabelInput
          label={translate('securityGrouping:form.modifiedBy')}
          value={metadata.modifiedBy}
          disabled
          data-testid="edit-security-grouping-modified-by-input"
        />
      </SidePanelFormLayout.Fields>

      <SidePanelFormLayout.Footer>
        <PermissionGuard action="edit">
          <PrimaryButton
            type="submit"
            fullWidth
            isLoading={isSubmitting}
            disabled={!isReady || isSubmitting}
          >
            {translate('common:actions.save')}
          </PrimaryButton>
        </PermissionGuard>
      </SidePanelFormLayout.Footer>
    </SidePanelFormLayout>
  );
};

export default EditSecurityGroupingFormPanel;
